import React, { useMemo } from "react"

import { useNavigate, useSearchParams } from "react-router-dom"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"

import { PageHelmetElement } from "@front-shared/elements/pageHelmet.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"
import { useScrollFix } from "@front-shared/hooks/scrollFix.hook"

import { GENERAL_CONFIG } from "../../config/general.config"
import { INSIGHTS_CONFIG } from "../../config/insights.config"
import { ROUTES_CONFIG } from "../../config/routes.config"
import { InsightsHelper } from "../../helpers/insights.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook.ts"
import { useDemoMode } from "../../hooks/useDemoMode.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { DashboardEmbedComponent } from "./dashboardEmbed.component.tsx"

import arrowLeftIcon from "@front-shared/assets/images/icons/arrow-left-dark.svg"

export const InsightsComponent: React.FunctionComponent = () => {
    useScrollFix()

    const navigate = useNavigate()

    useHasPermissions(OneVerifyPermission.viewPracticeInsights)

    const { isDemo } = useDemoMode()
    const runtimeState = useAppSelector(runtimeSelector)

    const [searchParams] = useSearchParams()

    const embed = useMemo(() => {
        return InsightsHelper.getEmbedFromSearchParams(searchParams)
    }, [searchParams])

    const fromPath = useMemo(() => {
        return InsightsHelper.getSafeFromPath(searchParams)
    }, [searchParams])

    const backLabel = useMemo(() => {
        return InsightsHelper.getBackLabel(fromPath)
    }, [fromPath])

    const onGoBack = () => {
        if (fromPath) {
            navigate(fromPath)

            return
        }

        navigate(ROUTES_CONFIG.insightsUrl)
    }

    return runtimeState.enabledFeatureFlags ? (
        <div
            data-produktly="insights-page"
            className="relative flex flex-col"
            style={{
                height: `calc(100vh - ${GENERAL_CONFIG.headerHeight}px)`,
            }}
        >
            <PageHelmetElement
                title="Dashboard"
                defaultPageTitle={GENERAL_CONFIG.defaultPageTitle}
            />

            {embed.isCustomEmbed ? (
                <div
                    className="
                        flex items-center px-[8px] py-[16px]
                        border-b border-solid border-brand-warmLight
                    "
                >
                    <div
                        className="p-[8px] mr-[8px] cursor-pointer"
                        onClick={onGoBack}
                    >
                        <img src={arrowLeftIcon} alt={backLabel} />
                    </div>

                    <PrimaryText typography="h3">{backLabel}</PrimaryText>
                </div>
            ) : null}

            {isDemo ? (
                <div
                    className="shrink-0 w-full mx-auto mt-[24px] mb-[8px] px-[24px]"
                    style={{
                        maxWidth: `${INSIGHTS_CONFIG.embedContainerMaxWidthPx}px`,
                    }}
                >
                    <div
                        className="
                            rounded-[8px] px-[16px] py-[14px]
                            bg-brand-lilacLight
                            border border-solid border-brand-warmLight2
                        "
                    >
                        <PrimaryText>{INSIGHTS_CONFIG.demoNotice}</PrimaryText>
                    </div>
                </div>
            ) : null}

            <div
                data-produktly="insights-dashboard"
                className={`
                    min-h-0 flex-1
                    ${embed.isCustomEmbed || isDemo ? "" : "mt-[24px]"}
                    ${isDemo ? "blur-[6px] pointer-events-none select-none" : ""}
                `}
            >
                <DashboardEmbedComponent
                    key={`${embed.embedType}-${embed.embedId}`}
                    style={{
                        height: "100%",
                    }}
                    embedType={embed.embedType}
                    embedId={embed.embedId}
                />
            </div>
        </div>
    ) : (
        <SpinnerElement containerClassName="text-center mt-[48px]" />
    )
}
